/**
 * 需給ギャップ 差分チャート (下段) の Y 軸 domain / ticks 算出。
 *
 * recharts 既定の auto-scale に任せると、 全コマがショート (= 全系列が負) の期間で
 * domain が負側だけに潰れ、 0 がプロット領域の最上端に重なる。 その結果
 * `<ReferenceLine y={0}>` が上辺と重なって切れ、 ロング側 (正方向) が 1px も
 * 描かれない (issue #6314「販売側のグラフが表示されない（0より下に伸びる）」)。
 *
 * spec `docs/specs/supply/supply-demand-gap.md` の 2 つの AC に従う:
 *   - AC-SDG-005: Y 軸は必ず 0 を含み、 **0 を中心とした対称レンジ** (−yBound 〜 +yBound)
 *     とする。 全期間ショートでもロング側の余白を確保する
 *   - AC-SDG-006: 目盛りはきりのいい刻み (1 / 2 / 5 × 10^n) で丸める
 *
 * **0 対称にするのはこの差分チャートだけ。** メインチャート (`./mainChartAxis`) の
 * 3 系列は非負なので、 対称にすると描画高の半分を空の負側に捨てることになる。
 * 符号を持つのは position (`ComputePosition` = 供給 − 需要) だけで、 ロングと
 * ショートを同じ縮尺で見比べる図はここだけ = AC-SDG-005 の対称レンジもここだけ。
 *
 * 入札後ポジション chart (`~/components/bidding/common/positionChartAxis.ts`) は
 * 同じ「0 が端に潰れる」事象 (issue #5209) を扱うが、 spot-bidding AC-BSP-016 が
 * 「0 の位置は固定しない」 と定めているため **非対称**に伸ばす。 本 chart は
 * AC-SDG-005 が対称レンジを明示しているので、 ロジックを共有せず別に持つ。
 */
import { niceScale, stepTicks } from '~/pages/SupplyGap/models/niceAxis'

/**
 * 0 の片側あたりの目盛り区間数 (= 目盛り線は 0 を含めて最大 5 本)。
 * 差分チャートは 2 枚組の下段 (flex 3) でプロット高が実測 100px 程度しかないため、
 * メイン軸 (`./mainChartAxis`) の 6 本より 1 本少ない 5 本に抑える (font 11px の
 * ラベルが詰まらない下限)。 対称レンジなので片側 2 区間 = 全体 4 区間 + 0。
 */
const MAX_SEGMENTS_PER_SIDE = 2

/**
 * 全コマ 0 / データ無しのときの片側の幅 (MW)。 0 幅の軸は描けないため。
 * 差分は供給力の ±10% 程度の規模なので、 小規模エリア (沖縄で ±十数 MW) から見て
 * 過大にならない値にする (mock-app `gapDiffAxis` と同じ 100MW)。
 */
const EMPTY_MAX_ABS_MW = 100

export type DiffYAxis = {
  /** recharts `<YAxis domain>` (= [−yBound, +yBound]。 0 を中心に対称)。 */
  domain: [number, number]
  /** recharts `<YAxis ticks>`。 0 を必ず含む昇順の目盛り値。 */
  ticks: number[]
}

/**
 * 差分チャートの Y 軸を 0 対称レンジで決める。
 *
 * 手順:
 *   1. 表示値の絶対値の最大 (`maxAbs`) を取る。 全コマ 0 / データ無しは
 *      {@link EMPTY_MAX_ABS_MW} を使う (0 幅の軸は描けないため)。
 *   2. 片側の区間数が {@link MAX_SEGMENTS_PER_SIDE} 以内に収まる最小の「きりのいい」
 *      刻み幅を選ぶ (最小の刻み幅 = 余白が最小 = 棒が描画高を最も広く使う)。
 *   3. `yBound = 区間数 × 刻み幅` を 0 の上下に同じだけ取る。 刻み幅は maxAbs の
 *      半分以上なので区間数は必ず 2 以内に収まり、 yBound は maxAbs の 2 倍未満になる。
 *
 * mock-app `gapDiffAxis` の 10% ヘッドルーム (`mirroredAxis(maxAbs, 1.1)`) は採らない。
 * 東京規模 (±370MW) では上乗せで刻みが 200 → 500 に跳ね、 上限が ±400 → ±500 と
 * 過大になるため。 代わりに maxAbs が刻みの整数倍のときは軸上端 = maxAbs となり、
 * 最大の棒が描画域の上辺に接する。
 *
 * @param values 差分チャートに描かれるポジション値 (MW、 符号付き)。
 */
export function computeDiffYAxis(values: readonly number[]): DiffYAxis {
  let maxAbs = 0
  for (const v of values) {
    if (!Number.isFinite(v)) continue
    const abs = Math.abs(v)
    if (abs > maxAbs) maxAbs = abs
  }
  if (maxAbs === 0) maxAbs = EMPTY_MAX_ABS_MW

  const { step, segments } = niceScale(maxAbs, MAX_SEGMENTS_PER_SIDE)
  const ticks = stepTicks(step, -segments, segments)
  const yBound = ticks[ticks.length - 1]
  return { domain: [-yBound, yBound], ticks }
}
